import { useEffect } from 'react';
import { Form, Modal, Radio, Space, Typography } from 'antd';
import { SelectAllMultiple, type SelectAllMultipleOption } from './shared/SelectAllMultiple';

export type BulkCategoryMode = 'replace' | 'append';

export interface BulkMoveCategoryValues {
  category_ids: string[];
  mode: BulkCategoryMode;
}

interface BulkMoveCategoryModalProps {
  open: boolean;
  count: number;
  loading?: boolean;
  categoryOptions: SelectAllMultipleOption[];
  onCancel: () => void;
  onSubmit: (values: BulkMoveCategoryValues) => void | Promise<void>;
}

/**
 * TCR-2484: chuyển danh mục cho nhiều sản phẩm cùng lúc. Ô danh mục dùng lại đúng
 * `SelectAllMultiple` của trang chi tiết sản phẩm — tìm kiếm, chọn nhiều — để người dùng
 * không phải học hai cách chọn danh mục.
 */
export function BulkMoveCategoryModal({ open, count, loading, categoryOptions, onCancel, onSubmit }: BulkMoveCategoryModalProps) {
  const [form] = Form.useForm<BulkMoveCategoryValues>();

  // Mỗi lần mở là một lượt mới — không giữ danh mục của lần chuyển trước.
  useEffect(() => {
    if (open) form.setFieldsValue({ category_ids: [], mode: 'replace' });
  }, [form, open]);

  return (
    <Modal
      open={open}
      title={`Chuyển danh mục hàng loạt (Đã chọn ${count} sản phẩm)`}
      okText="Xác nhận"
      cancelText="Hủy"
      confirmLoading={loading}
      onOk={() => form.submit()}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Form<BulkMoveCategoryValues>
        form={form}
        layout="vertical"
        initialValues={{ category_ids: [], mode: 'replace' }}
        onFinish={(values) => onSubmit({ category_ids: values.category_ids, mode: values.mode })}
      >
        <Form.Item
          label="Danh mục mới"
          name="category_ids"
          rules={[{ required: true, type: 'array', min: 1, message: 'Chọn ít nhất 1 danh mục' }]}
        >
          <SelectAllMultiple options={categoryOptions} placeholder="Chọn danh mục" />
        </Form.Item>
        <Form.Item label="Cách cập nhật" name="mode">
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="replace">
                Thay thế hoàn toàn
                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  Xoá toàn bộ danh mục cũ của các sản phẩm đã chọn và gán danh mục mới.
                </Typography.Text>
              </Radio>
              <Radio value="append">
                Thêm vào danh mục hiện có
                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  Giữ danh mục cũ, thêm danh mục mới cho các sản phẩm chưa có.
                </Typography.Text>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
      </Form>
    </Modal>
  );
}
